/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { ActiveMapModel, CustomGeoPosition, DisplayFeatures } from './active-map.model';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import {
    CopyCoordinates,
    DisableGeolocation,
    DisableMapTip,
    EnableCompassReading,
    EnableGeolocation,
    EnableMapTip,
    GoogleMapInitialized,
    InitializeMapState,
    LaunchStreetView,
    MapCreated,
    MapDestroyed,
    MergeLayerDisplayFeatures,
    RefreshMap,
    ReloadMap,
    ResetMapState,
    SetCompassDirection,
    SetGeolocationPosition,
    SetGoogleMapType,
    SetGoogleMaxImageryZoom,
    SetGoogleMaxNonImageryZoom,
    SetLayerDisplayFeatures,
    SetMapCenter,
    SetMapExtent,
    SetMapZoom,
    ToggleMapTipEnabled,
    UserSelection,
} from './active-map.actions';
import { ClearBuffer, CreateBuffer } from '../command/buffer/buffer.actions';
import { DashGisUtil } from '../../shared/dash-gis-util';
import { MapConfigModel } from './map-config.model';
import { ClearSearchResultFeatures, DisplaySearchResultFeatures } from '../../search/state/search.actions';
import { LayerStateChange } from './map-config.actions';
import { Injectable } from '@angular/core';
import { append, patch } from '@ngxs/store/operators';
import { from } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ShowSuccess, ShowWarning } from '../../../core/state/notification/notification.actions';
import { AppConfigState } from '../../../core/state/appConfig/app-config.state';
import { Logger } from '@angular/compiler-cli';
import { LoggerService } from '../../shared/services/logger.service';

export const MAP_DEFAULT_STATE: ActiveMapModel = {
    initialized: false,
    created: false,
    googleInitialized: false,
    center: [0, 0],
    extent: [-1000, -1000, 1000, 1000],
    zoom: 14,
    scale: 3000,
    googleLayerType: 'roadmap',
    maxImageryZoom: 19,
    maxNonImageryZoom: 20,
    maxImageryZoomCenter: [0, 0],
    maxNonImageryZoomCenter: [0, 0],
    displayFeatures: {},
    mapTipEnabled: false, // start disabled until map is loaded
    geolocationEnabled: false,
    position: {
        latitude: null,
        longitude: null,
        heading: null,
        accuracy: null,
    },
    direction: 0,
};

@State<ActiveMapModel>({
    name: 'map',
    defaults: MAP_DEFAULT_STATE
})
@Injectable()
export class ActiveMapState {
    constructor(
        private store: Store,
        private logger: LoggerService,
    ) {}

    @Action(InitializeMapState)
    initializeMapState({ getState, patchState }, action: InitializeMapState) {
        const mapConfig: Partial<MapConfigModel> = action.payload;
        const mapState: ActiveMapModel = getState();
        if (!mapState.initialized) {
            patchState({ ...mapConfig, ...{ initialized: true } });
        }

        // initial google map type is loaded in AppConfig
        const initialLayer = this.store.selectSnapshot(AppConfigState.baseLayer)?.initialLayer;
        if (initialLayer) {
            patchState({ googleLayerType: initialLayer });
        }
    }

    @Action(MapCreated)
    mapCreated({ getState, patchState }: StateContext<ActiveMapModel>) {
        const state = getState();
        if (!state.initialized) {
            // get initial values from MapConfigState
            const configState = this.store.selectSnapshot((s) => s.mapConfig.config);
            patchState({
                center: configState.center,
                extent: configState.extent,
            });
        }
        patchState(({created: true}));
    }

    @Action(GoogleMapInitialized)
    googleMapInitialized({ setState}: StateContext<ActiveMapModel>) {
        setState(patch({googleInitialized: true}));
    }

    @Action(MapDestroyed)
    mapDestroyed({ setState }: StateContext<ActiveMapModel>) {
        setState(patch({ created: false }));
    }

    @Action(ResetMapState)
    reset({ setState }: StateContext<ActiveMapModel>) {
        setState(MAP_DEFAULT_STATE);
    }

    @Action(ReloadMap)
    /**
     * Sends signal to reload map from server, resetting center, zoom, and visible layers
     */
    reload() {
        // no action
    }

    @Action(RefreshMap)
    /**
     * Sends signal to refresh map from server keeping current center, zoom, and visible layers
     */
    refreshMap() {
        // no action
    }

    @Action(SetMapCenter, { cancelUncompleted: true })
    setCenter({ setState }: StateContext<ActiveMapModel>, action: SetMapCenter) {
        setState(patch({ center: action.payload }));
    }

    @Action(SetMapExtent)
    setExtent({ setState }: StateContext<ActiveMapModel>, action: SetMapExtent) {
        setState(patch({ extent: action.payload }));
    }

    @Action(SetMapZoom, { cancelUncompleted: true })
    setZoom({ setState, dispatch }: StateContext<ActiveMapModel>, action: SetMapZoom) {
        setState(
            patch({
                zoom: action.payload.zoom,
                scale: action.payload.scale
            })
        );
        dispatch(new LayerStateChange());
    }

    @Action(SetGoogleMapType)
    setGoogleMapType({ setState }: StateContext<ActiveMapModel>, action: SetGoogleMapType) {
        setState(patch({ googleLayerType: action.payload }));
    }

    @Action(SetGoogleMaxImageryZoom)
    setGoogleMaxImageryZoom({ setState }: StateContext<ActiveMapModel>, action: SetGoogleMaxImageryZoom) {
        setState(
            patch({
                maxImageryZoom: action.payload.zoom,
                maxImageryZoomCenter: action.payload.center
            })
        );
    }

    @Action(SetGoogleMaxNonImageryZoom)
    setGoogleMaxNonImageryZoom({ setState }: StateContext<ActiveMapModel>, action: SetGoogleMaxNonImageryZoom) {
        setState(
            patch({
                maxNonImageryZoom: action.payload.zoom,
                maxNonImageryZoomCenter: action.payload.center,
            })
        );
    }

    @Action(EnableMapTip)
    enableMapTip({ setState }: StateContext<ActiveMapModel>) {
        setState(patch({ mapTipEnabled: true }));
    }

    @Action(DisableMapTip)
    disableMapTip({ setState }: StateContext<ActiveMapModel>) {
        setState(patch({ mapTipEnabled: false }));
    }

    @Action(ToggleMapTipEnabled)
    toggleMaptip({ getState, setState }: StateContext<ActiveMapModel>) {
        const state = DashGisUtil.clone(getState());
        state.mapTipEnabled = !state.mapTipEnabled;
        setState(state);
    }

    @Action([ UserSelection ])
    userSelection() {}

    @Action(SetLayerDisplayFeatures)
    updateDisplayFeatures({ setState }: StateContext<ActiveMapModel>, action: SetLayerDisplayFeatures) {
        setState(
            patch({
                displayFeatures: patch({
                    [action.payload.displayLayer]: action.payload.featureIds
                })
            })
        );
    }

    @Action(DisplaySearchResultFeatures)
    displaySearchResultFeatures(
        { setState }: StateContext<ActiveMapModel>,
        action: DisplaySearchResultFeatures
    ) {
        setState(
            patch({
                displayFeatures: patch({
                    [action.payload.displayLayer]: append(action.payload.featureIds)
                })
            })
        );
    }

    @Action(ClearSearchResultFeatures)
    clearSearchResultFeatures({ setState }: StateContext<ActiveMapModel>, action: ClearSearchResultFeatures) {
        setState(
            patch({
                displayFeatures: patch({
                    [action.payload.displayLayer]: [] as string[]
                })
            })
        );
    }

    /**
     * Merge a set of feature ids into the existing set of ids for a layer. This will
     * actually toggle the display of a feature that already exists
     */
    @Action(MergeLayerDisplayFeatures)
    mergeDisplayFeatures({ getState, setState }: StateContext<ActiveMapModel>, action: MergeLayerDisplayFeatures) {
        const newState: ActiveMapModel = DashGisUtil.clone(getState());
        const layer = action.payload.displayLayer;

        let existingFeatureIds: string[];
        if (newState.displayFeatures[layer]) {
            existingFeatureIds = newState.displayFeatures[layer];
        } else {
            existingFeatureIds = [];
        }
        action.payload.featureIds.forEach((featureId: string) => {
            const idx = existingFeatureIds.findIndex((f) => featureId === f);
            if (idx === -1) {
                existingFeatureIds.push(featureId);
            } else {
                existingFeatureIds.splice(idx, 1, featureId);
            }
        });
        newState.displayFeatures[layer] = existingFeatureIds;
        setState(newState);
    }

    @Action(CreateBuffer)
    createBuffer() {}

    @Action(ClearBuffer)
    clearBuffer({ setState }: StateContext<ActiveMapModel>) {
        setState(
            patch({
                displayFeatures: patch({ buffer: [] })
            })
        );
    }

    @Action(SetGeolocationPosition)
    setPosition({ setState }: StateContext<ActiveMapModel>, action: SetGeolocationPosition) {
        setState(patch({ position: action.payload }));
    }

    @Action(EnableGeolocation)
    enableGeolocation({ setState }: StateContext<ActiveMapModel>) {
        setState(patch({ geolocationEnabled: true }));
    }

    @Action(DisableGeolocation)
    disableGeolocation({ setState }: StateContext<ActiveMapModel>) {
        setState(patch({ geolocationEnabled: false }));
    }

    @Action(SetCompassDirection)
    setCompassDirection({ setState }: StateContext<ActiveMapModel>, action: SetCompassDirection) {
        setState(patch({ direction: action.direction }));
    }

    @Action(LaunchStreetView)
    launchStreetView() {}

    @Action(CopyCoordinates)
    copyCoordinates({ dispatch }: StateContext<ActiveMapModel>, action: CopyCoordinates) {
        return from(navigator.clipboard.writeText(`${ action.coordinates[1] },${ action.coordinates[0] }`)).pipe(
            catchError(() => dispatch(new ShowWarning('Could not write Lat/Lon to the clipboard.'))),
            tap(() => dispatch(new ShowSuccess('Lat/Lon copied to the clipboard.')))
        );
    }

    @Selector()
    static initialized(state: ActiveMapModel) {
        return state.initialized;
    }

    @Selector()
    static created(state: ActiveMapModel) {
        return state.created;
    }

    @Selector()
    static googleMapInitialized(state: ActiveMapModel) {
        return state.googleInitialized;
    }

    @Selector()
    static state(state: ActiveMapModel) {
        return state;
    }

    @Selector()
    static center(state: ActiveMapModel) {
        return state.center;
    }

    @Selector()
    static extent(state: ActiveMapModel) {
        return state.extent;
    }

    @Selector()
    static zoom(state: ActiveMapModel) {
        return state.zoom;
    }

    @Selector()
    static scale(state: ActiveMapModel) {
        return state.scale;
    }

    @Selector()
    static mapTipEnabled(state: ActiveMapModel) {
        return state.mapTipEnabled;
    }

    @Selector()
    static googleLayerType(state: ActiveMapModel) {
        return state.googleLayerType;
    }

    @Selector()
    static maxImageryZoom(state: ActiveMapModel) {
        return state.maxImageryZoom;
    }

    @Selector()
    static maxImageryZoomCenter(state: ActiveMapModel) {
        return state.maxImageryZoomCenter;
    }

    @Selector()
    static maxNonImageryZoom(state: ActiveMapModel) {
        return state.maxNonImageryZoom;
    }

    @Selector()
    static maxNonImageryZoomCenter(state: ActiveMapModel) {
        return state.maxNonImageryZoomCenter;
    }

    @Selector()
    static displayFeatures(state: ActiveMapModel): DisplayFeatures {
        return state.displayFeatures;
    }

    @Selector()
    static geolocationEnabled(state: ActiveMapModel): boolean {
        return state.geolocationEnabled;
    }

    @Selector()
    static position(state: ActiveMapModel): CustomGeoPosition {
        return state.position;
    }

    @Selector()
    static direction(state: ActiveMapModel): number {
        return state.direction;
    }
}
